import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, ReplaySubject} from 'rxjs';
import {tap} from 'rxjs/operators';


export interface LoggingConfig {
  level: number,
}

export interface AuthConfig {
  authority: string,
  clientId: string,
  scope: string,
  logLevel: number,
  renewTimeBeforeTokenExpiresInSeconds: number,
}

export interface ExportConfig {
  apiBaseUrl: string,
}

export interface PortalConfig {
  targetOrigin: string,
}

export interface SentryConfig {
  enabled: boolean,
  debug: boolean,
  dsn: string,
  sampleRate: number,
}

export interface MatomoConfig {
  enabled: boolean,
  trackerUrl: string,
  siteId: string,
}

export interface IConfig {
  environment: string,
  logging: LoggingConfig,
  auth: AuthConfig,
  exportConfig: ExportConfig,
  portal: PortalConfig,
  sentry: SentryConfig,
  matomo: MatomoConfig,
}

export class Config
  implements IConfig {

  constructor(
    public environment: string,
    public logging: LoggingConfig,
    public auth: AuthConfig,
    public exportConfig: ExportConfig,
    public portal: PortalConfig,
    public sentry: SentryConfig,
    public matomo: MatomoConfig,
  ) {
  }
}

@Injectable()
export class ConfigService {

  private readonly _destroyRef = inject(DestroyRef);

  private loaded = new ReplaySubject<Config>();

  private config_?: Config;

  constructor(
    private httpClient: HttpClient,
  ) {
  }

  get loaded$() {
    return this.loaded.pipe(
      takeUntilDestroyed(this._destroyRef),
    );
  }

  get config(): Config {
    return this.config_ as Config;
  }

  loadConfig() {
    return forkJoin([
      this.getConfig(),
    ]);
  }

  getConfig(): Observable<Config> {
    const url = '/assets/config/config.json';
    return this.httpClient.get<IConfig>(url)
      .pipe(
        tap(config => {
          this.config_ = new Config(
            config.environment,
            config.logging,
            config.auth,
            config.exportConfig,
            config.portal,
            config.sentry,
            config.matomo,
          );
          this.loaded.next(this.config_);
        }),
      );
  }
}
